import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/outline";
import { Checkbox } from "components/ui/checkbox";
import { PrimaryButton } from "components/ui/primary-button";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useEffect, useState } from "react";

export type InlineFeedbackResponse = "positive" | "negative" | null;

export interface InlineFeedbackProps {
    negativeFeedbackPrompt: string;
    negativeFeedbackOptions: string[];
    submitted: InlineFeedbackResponse;
    submitting: boolean;
    positiveAriaLabel: string;
    negativeAriaLabel: string;
    onPositive: () => void;
    onNegativeIntent: () => void;
    onNegativeSubmit: (combinedText: string | null) => void;
}

export const InlineFeedback: React.FC<InlineFeedbackProps> = ({
    negativeFeedbackPrompt,
    negativeFeedbackOptions,
    submitted,
    submitting,
    positiveAriaLabel,
    negativeAriaLabel,
    onPositive,
    onNegativeIntent,
    onNegativeSubmit,
}) => {
    const [showForm, setShowForm] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [feedbackText, setFeedbackText] = useState("");

    useEffect(() => {
        if (submitted !== "negative") {
            setShowForm(false);
            setSelectedOptions([]);
            setFeedbackText("");
        }
    }, [submitted]);

    const handleThumbsDown = useCallback(() => {
        setShowForm(true);
        onNegativeIntent();
    }, [onNegativeIntent]);

    const handleToggleOption = useCallback((option: string) => {
        setSelectedOptions(prev =>
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option],
        );
    }, []);

    const handleSubmit = useCallback(() => {
        const trimmedText = feedbackText.trim();
        const parts = [
            ...selectedOptions,
            trimmedText.length > 0 ? trimmedText : null,
        ].filter((part): part is string => Boolean(part));
        const combined = parts.join("; ");
        onNegativeSubmit(combined.length > 0 ? combined : null);
        setShowForm(false);
    }, [feedbackText, selectedOptions, onNegativeSubmit]);

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onPositive}
                    disabled={submitting}
                    className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                        submitted === "positive"
                            ? "text-green-600 bg-green-50"
                            : "text-gray-400 hover:text-gray-600"
                    }`}
                    aria-label={positiveAriaLabel}>
                    <HandThumbUpIcon className="w-5 h-5" />
                </button>
                <button
                    type="button"
                    onClick={handleThumbsDown}
                    disabled={submitting}
                    className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                        submitted === "negative"
                            ? "text-gray-700 bg-gray-100"
                            : "text-gray-400 hover:text-gray-600"
                    }`}
                    aria-label={negativeAriaLabel}>
                    <HandThumbDownIcon className="w-5 h-5" />
                </button>
            </div>

            {showForm && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md space-y-3 transition-all">
                    <p className="text-sm font-medium text-gray-700">
                        {negativeFeedbackPrompt}
                    </p>
                    <div className="space-y-2">
                        {negativeFeedbackOptions.map(option => (
                            <Checkbox
                                key={option}
                                label={option}
                                checked={selectedOptions.includes(option)}
                                onChange={() => handleToggleOption(option)}
                            />
                        ))}
                    </div>
                    <TextArea
                        value={feedbackText}
                        onChange={e => setFeedbackText(e.target.value)}
                        placeholder="Additional feedback (optional)"
                        rows={3}
                        textSize="sm"
                    />
                    <PrimaryButton onClick={handleSubmit} disabled={submitting}>
                        {submitting ? "Sending..." : "Send Feedback"}
                    </PrimaryButton>
                </div>
            )}
        </div>
    );
};
